import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  AnalyticsPage,
  BigNumber,
  DurationCard,
  formatCardPercent,
  formatCount,
  formatFraction,
  METRIC_HELP,
  MetricCardFrame,
  RateCard,
  SectionHeading,
  SentinelReport,
  SERIES_VALUE,
  TrendChart,
} from '@/modules/sentinel-analytics';
import type { SentinelAnalyticsReport } from '@/types/sentinelAnalytics';

type OverviewTab = 'ticket' | 'message';

function TicketTab({ report }: { report: SentinelAnalyticsReport }) {
  const { tickets, series, businessTime } = report;
  return (
    <>
      <SectionHeading>Intake</SectionHeading>
      <div className='mt-3'>
        <MetricCardFrame title='Total Tickets' help={METRIC_HELP.totalTickets}>
          <BigNumber value={formatCount(tickets.totalTickets)} className='mt-auto pt-6' />
        </MetricCardFrame>
      </div>
      <SectionHeading>Reopens</SectionHeading>
      <div className='mt-3 grid gap-x-3 gap-y-3 lg:grid-cols-2'>
        <RateCard title='Reopen Ticket Rate' help={METRIC_HELP.reopen} metric={tickets.reopen} unit='' trend={{ series, value: SERIES_VALUE.reopen }} />
        <RateCard title='FTNR' help={METRIC_HELP.ftnr} metric={tickets.ftnr} unit='' trend={{ series, value: SERIES_VALUE.ftnr }} />
      </div>
      <SectionHeading>Quality</SectionHeading>
      <div className='mt-3 grid gap-x-4 gap-y-3 lg:grid-cols-2'>
        <MetricCardFrame title='CSAT' help={METRIC_HELP.csat}>
          <BigNumber
            value={tickets.csat.average === null ? '—' : tickets.csat.average.toFixed(1)}
            detail='/5'
            inline
            className='mt-auto pt-6'
          />
        </MetricCardFrame>
        <MetricCardFrame title='CSAT Response Rate' help={METRIC_HELP.csatResponse}>
          <BigNumber value={formatCardPercent(tickets.csatResponse.rate)} detail={<span className='pl-2.5'>{formatFraction(tickets.csatResponse)}</span>} inline className='mt-auto pt-6' />
        </MetricCardFrame>
      </div>
      <div className='mt-3 grid gap-x-4 gap-y-3 lg:grid-cols-2'>
        <DurationCard title='FRT' help={METRIC_HELP.firstResponse} metric={tickets.firstResponse} businessAvailable={businessTime.available} averageLabel='Average FRT' trend={{ series, value: { business: SERIES_VALUE.firstResponseBusinessMinutes, calendar: SERIES_VALUE.firstResponseMinutes } }} />
        <DurationCard title='Resolution time' help={METRIC_HELP.resolution} metric={tickets.resolution} businessAvailable={businessTime.available} trend={{ series, value: { business: SERIES_VALUE.resolutionBusinessMinutes, calendar: SERIES_VALUE.resolutionMinutes } }} />
      </div>
    </>
  );
}

function MessageTab({ report }: { report: SentinelAnalyticsReport }) {
  const { messages, series } = report;
  return (
    <>
      <div className='mt-3 grid gap-x-[19px] gap-y-3 lg:grid-cols-3'>
        <MetricCardFrame title='Corrected Responses' help={METRIC_HELP.correctedResponses}>
          <BigNumber value={formatCount(messages.correctedResponses.count)} className='mt-auto pt-6' />
        </MetricCardFrame>
        <MetricCardFrame title='Accurate AI Responses' help={METRIC_HELP.accurateResponses}>
          <BigNumber value={formatCount(messages.accurateResponses.count)} className='mt-auto pt-6' />
        </MetricCardFrame>
        <MetricCardFrame title='AI Accuracy Rate' help={METRIC_HELP.aiAccuracy}>
          <BigNumber value={formatCardPercent(messages.accurateResponses.rate)} detail={formatFraction(messages.accurateResponses)} inline className='mt-auto pt-6' />
        </MetricCardFrame>
      </div>
      <div className='mt-6'>
        <MetricCardFrame title='AI accuracy trend' help={METRIC_HELP.aiAccuracyTrend}>
          <BigNumber value={formatCardPercent(messages.accurateResponses.rate)} suffix='Avg.' detail={`${formatFraction(messages.accurateResponses)} graded drafts`} />
          <TrendChart series={series} value={SERIES_VALUE.aiAccuracy} unit='percent' label='AI accuracy' markDays />
        </MetricCardFrame>
      </div>
    </>
  );
}

export default function SupportOverviewRoute() {
  const [tab, setTab] = useState<OverviewTab>('ticket');
  return (
    <AnalyticsPage title='Overview'>
      {(query) => <SentinelReport query={query}>{(report) => (
        <>
          <div role='tablist' aria-label='Overview metrics' className='mt-3 mb-3 inline-flex rounded-full bg-gray-100 p-0.5 text-[13px] leading-[18px]'>
            {(['ticket', 'message'] as const).map((option) => (
              <button
                key={option}
                type='button'
                role='tab'
                aria-selected={tab === option}
                onClick={() => setTab(option)}
                className={cn('rounded-full border border-transparent px-[11px] py-[5px] text-gray-800', tab === option && 'border-gray-500 bg-white font-medium text-ink-strong shadow-[0_3px_12px_rgba(0,0,0,0.08)]')}
              >
                {option === 'ticket' ? 'Ticket Metrics' : 'Message Metrics'}
              </button>
            ))}
          </div>
          {tab === 'ticket' ? <TicketTab report={report} /> : <MessageTab report={report} />}
        </>
      )}</SentinelReport>}
    </AnalyticsPage>
  );
}
